import { Injectable } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BehaviorSubject, Subject, catchError, throwError } from 'rxjs';
import { AssignmentGroupInteractionService } from '../../../system-admin/assignment-group/services/assignment-group-interaction.service';
import { EmployeesTrainingPlanService } from './employees-training-plan.service';
import { EmployeeTrainingPlanItems } from '../models/employeeTrainingPlanItem';
import { EmployeesTrainingPlanConstants } from '../employees-training-plan.constants';
import { CourseCatalogueSearchResult } from '../../../course-catalogue/models/course-catalogue-search-result';
import { EmployeesTrainingPlanDetails } from '../models/employeeTrainingPlanDetails';
import { EmployeeTrainingPlan } from '../models/employeeTrainingPlan';
import { TrainingItemGroupDetails } from '../../../training-item/models/training-item-group-detail';
import { TrainingItemConstants } from '../../../training-item/constants/training-item-constants';

@Injectable({
  providedIn: 'root'
})
export class EmployeesTrainingPlanPersistenceService {
  closeAndResetTrainingPlanSlider$ = new Subject<boolean>();
  TrainingPlanSaveInProgress$ = new BehaviorSubject<boolean>(false);
  currentTabInSliderMenu$ = new Subject<number>();
  currentTabInSliderMenuByCustomChange$ = new Subject<number>();
  populateErrorsForTab$ = new BehaviorSubject<boolean>(false);
  errorTabIndices: number[] = [];
  trainingItemsList: EmployeeTrainingPlanItems[] = []
  duplicateTrainingItemsNumber: string[] = [];
  employeeTrainingPlan$ = new BehaviorSubject<EmployeeTrainingPlan | null>(null);
  employeesTrainingPlanDetails !: EmployeesTrainingPlanDetails;

  private trainingPlanUpdated = new Subject<any>();
  data$ = this.trainingPlanUpdated.asObservable();
  
  private toastMessage = new Subject<any>();
  toastMessage$ = this.toastMessage.asObservable();
 
  constructor(private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private employeesTrainingPlanService: EmployeesTrainingPlanService,
    private assignmentGroupInteractionService: AssignmentGroupInteractionService) { }

    openConfirmationDialog = () => {
      this.confirmationService.confirm({
        message: EmployeesTrainingPlanConstants.TrainingPlan_Slider_Confirmation_Text,
        header: EmployeesTrainingPlanConstants.TrainingPlan_Slider_Confirmation_Header,
        icon: 'pi pi-exclamation-triangle',
        acceptIcon: 'pi pi-check',
        rejectIcon: 'pi pi-times',
        accept: () => {
          this.closeAndResetTrainingPlanSlider$.next(true);
        },
      });
      // TODO - Remove once the the confirmation service popup starts working
      this.closeAndResetTrainingPlanSlider$.next(true);
    }

    onTabChange = (tabIndex: number) => {
      this.currentTabInSliderMenu$.next(tabIndex);
    }

    onCustomTabChange = (tabIndex: number) => {
      this.currentTabInSliderMenuByCustomChange$.next(tabIndex);
    }

    updateTabErrorIndex = (tabIndex: number, isError: boolean) => {
      isError ? this.addErrorTabIndex(tabIndex) : this.removeErrorTabIndex(tabIndex);
      this.populateErrorsForTab$.next(true);
    }

    // To Add Error Index On Tab Changes By Tab Menu Or Using Next/Previous Button
    addErrorTabIndex = (tabIndex: number) => {
      if (this.errorTabIndices.indexOf(tabIndex) === -1) {
        this.errorTabIndices.push(tabIndex);
      }
    }

    // To Remove Error Index On Tab Changes By Tab Menu Or Using Next/Previous Button
    removeErrorTabIndex = (tabIndex: number) => {
      if (this.errorTabIndices.indexOf(tabIndex) > -1) {
        this.errorTabIndices.splice(this.errorTabIndices.indexOf(tabIndex), 1);
      }
    }

    UpdateEmployeeTrainingPlaDetails = (employeeTrainingPlan: EmployeeTrainingPlan) => {
      this.employeeTrainingPlan$.next(employeeTrainingPlan);
    }

    // To prepare Course data for Training Requirement
    prepareDataForCourseTrainingItem(courses: CourseCatalogueSearchResult[], userPUID?: string){
      this.trainingItemsList = []
      courses.forEach(course => {
        const trainingItem: EmployeeTrainingPlanItems = this.createEmployeeTrainingPlanItem(course.courseNumber, EmployeesTrainingPlanConstants.EmployeeTraining_CourseType, null);
        this.trainingItemsList.push(trainingItem);
    })

      var trainingPlan: EmployeesTrainingPlanDetails = {
        userPUID: userPUID,
        trainingItems: this.trainingItemsList
      }
      this.employeesTrainingPlanDetails = trainingPlan;
    }


    // To prepare Document data for Training Requirement
    prepareDataForDocumentTrainingItem(documents: any[], userPUID?: string) {
      this.trainingItemsList = [];
      documents.forEach(doc => {
        const trainingItem: EmployeeTrainingPlanItems = this.createEmployeeTrainingPlanItem(doc.number, EmployeesTrainingPlanConstants.EmployeeTraining_DocumentType, null);
        this.trainingItemsList.push(trainingItem);
      })
      var trainingPlan: EmployeesTrainingPlanDetails = {
        userPUID: userPUID,
        trainingItems: this.trainingItemsList
      }
      this.employeesTrainingPlanDetails = trainingPlan;
    }

    // To prepare Training Item data for Training Requirement
    prepareDataForTrainingItemGroupTrainingItem(trainingItemGroups: TrainingItemGroupDetails[], userPUID?: string) {
      this.trainingItemsList = [];
      
      trainingItemGroups.forEach((trainingItemGroup) => {
        trainingItemGroup.trainingItems.forEach((trainingItem) => {
          if (this.trainingItemsList.length > 0) {
            //If the training item is already a part of the curriculum
            if (this.trainingItemsList.filter((til) => {
              return til.id === trainingItem.trainingItemNumber
            }).length > 0) {
              this.duplicateTrainingItemsNumber.push(trainingItem.trainingItemNumber);
              //if the training item group is already set some value
              if (this.trainingItemsList.filter((til) => {
                return til.id === trainingItem.trainingItemNumber
              })[0]!.trainingItemGroups !== undefined && this.trainingItemsList.filter((til) => {
                return til.id === trainingItem.trainingItemNumber
              })[0]!.trainingItemGroups !== null) {
                if (!this.trainingItemsList.filter((til) => {
                  return til.id === trainingItem.trainingItemNumber
                })[0]!.trainingItemGroups?.some(((tig) => { return tig === trainingItemGroup.number })))
                  this.trainingItemsList.filter((til) => {
                    return til.id === trainingItem.trainingItemNumber
                  })[0]!.trainingItemGroups!.push(trainingItemGroup.number);
              } else {
                //if the training item has no prior training item group
                this.trainingItemsList.filter((til) => {
                  return til.id === trainingItem.trainingItemNumber
                })[0]!.trainingItemGroups = [trainingItemGroup.number];
              }
            } else {
              //create training item newly
              if (trainingItem.trainingItemType === TrainingItemConstants.Training_Item_Type_Document) {
                let item: EmployeeTrainingPlanItems = this.createEmployeeTrainingPlanItem(trainingItem.trainingItemNumber,
                  EmployeesTrainingPlanConstants.EmployeeTraining_DocumentType, trainingItemGroup.number);
                this.trainingItemsList.push(item);
              } else if (trainingItem.trainingItemType === TrainingItemConstants.Training_Item_Type_Course) {
                let item: EmployeeTrainingPlanItems = this.createEmployeeTrainingPlanItem(trainingItem.trainingItemNumber,
                  EmployeesTrainingPlanConstants.EmployeeTraining_CourseType, trainingItemGroup.number);
                this.trainingItemsList.push(item);
              }
            }
          } else {
            if (trainingItem.trainingItemType === TrainingItemConstants.Training_Item_Type_Document) {
              let item: EmployeeTrainingPlanItems = this.createEmployeeTrainingPlanItem(trainingItem.trainingItemNumber,
                EmployeesTrainingPlanConstants.EmployeeTraining_DocumentType, trainingItemGroup.number);
              this.trainingItemsList.push(item);
            } else if (trainingItem.trainingItemType === TrainingItemConstants.Training_Item_Type_Course) {
              let item: EmployeeTrainingPlanItems = this.createEmployeeTrainingPlanItem(trainingItem.trainingItemNumber,
                EmployeesTrainingPlanConstants.EmployeeTraining_CourseType, trainingItemGroup.number);
              this.trainingItemsList.push(item);
            }
          }
        })
      })
      var trainingPlan: EmployeesTrainingPlanDetails = {
        userPUID: userPUID,
        trainingItems: this.trainingItemsList
      }
      this.employeesTrainingPlanDetails = trainingPlan;
    }

    
  
    updateTrainingPlan(addAndClose: boolean){
      this.employeesTrainingPlanService.updateTrainingPlanForSelectedUser(this.employeesTrainingPlanDetails)
        .pipe(
          catchError((err) => {
            this.messageService.add({
              severity: EmployeesTrainingPlanConstants.Severity_Err,
              summary: `${err.error}`,
            });
            return throwError(() => err);
          })
        ).subscribe((response: any) => {
          // Show summary message based on response
          if (response?.addedItems?.length > 0 && response?.skippedItems?.length > 0) {
            // Mixed result - show warning
            const skippedList = response.skippedItems.join(', ');
            const warningMessage = EmployeesTrainingPlanConstants.LinkedItems_AlreadyExists_InTrainingPlan.replace(
              'ItemNumbers',
              skippedList
            );
            
            this.toastMessage.next({
              severity: EmployeesTrainingPlanConstants.Severity_Warning,
              summary: warningMessage,
              life: 8000
            });
            
            // Also show success for added items
            this.toastMessage.next({
              severity: EmployeesTrainingPlanConstants.Severity_Success,
              summary: EmployeesTrainingPlanConstants.TrainingPlan_updatedSuccesfully + this.employeesTrainingPlanDetails?.userPUID,
              life: 8000
            });
          } else if (response?.addedItems?.length > 0) {
            // All added successfully
            this.toastMessage.next({
              severity: EmployeesTrainingPlanConstants.Severity_Success,
              summary: EmployeesTrainingPlanConstants.TrainingPlan_updatedSuccesfully + this.employeesTrainingPlanDetails?.userPUID,
              life: 8000
            });
          } else if (response?.skippedItems?.length > 0) {
            // All skipped - already exist
            const skippedList = response.skippedItems.join(', ');
            const warningMessage = EmployeesTrainingPlanConstants.LinkedItems_AlreadyExists_InTrainingPlan.replace(
              'ItemNumbers',
              skippedList
            );
            
            this.toastMessage.next({
              severity: EmployeesTrainingPlanConstants.Severity_Warning,
              summary: warningMessage,
              life: 8000
            });
          }
          
          this.assignmentGroupInteractionService.updatePanelListOnCreateOrEdit();
          this.sendData(true);
          
          if(addAndClose)
            this.closeAndResetTrainingPlanSlider$.next(true);
        })
  }

    addTrainingPlan(addAndClose: boolean){
      this.updateTrainingPlan(addAndClose);
    }

    private createEmployeeTrainingPlanItem(id: string, type: number, trainingItemGroupNumber: string | null): EmployeeTrainingPlanItems  {
      let employeeTrainingItem: EmployeeTrainingPlanItems = {
        id: id,
        type: type,
        status: EmployeesTrainingPlanConstants.TrainingPlan_StatusActive,
        coreOrElective: EmployeesTrainingPlanConstants.TrainingPlan_Core,
        trainingItemGroups: trainingItemGroupNumber !== null ? [trainingItemGroupNumber] : undefined
      };
      return employeeTrainingItem;
  }

  sendData(data: any) {
    this.trainingPlanUpdated.next(data);
  }

}
